import { Injectable } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Bandeau « Une nouvelle version est disponible » (service worker Angular).
 *
 * FAUX POSITIF CORRIGÉ
 * ngsw-config est en `navigationRequestStrategy: "freshness"` : au rechargement,
 * index.html vient du réseau, la page charge donc DÉJÀ les bundles de la
 * dernière version. Le service worker, lui, avait encore l'ancienne en cache ;
 * en téléchargeant la nouvelle il émet VERSION_READY… et le bandeau proposait
 * de mettre à jour une version qu'on utilisait déjà.
 *
 * Avant d'afficher le bandeau, on vérifie donc que le bundle principal chargé
 * par la page (main-<hash>.js) n'appartient PAS à la nouvelle version (manifeste
 * ngsw.json lu hors service worker). S'il en fait partie, on aligne le service
 * worker en silence (activateUpdate, sans rechargement) et rien ne s'affiche.
 */
@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {
  private readonly updateAvailable = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$ = this.updateAvailable.asObservable();

  private listening = false;

  constructor(private swUpdate: SwUpdate) {}

  /** À appeler une fois au démarrage (AppComponent). */
  public checkForUpdate(): void {
    if (!this.swUpdate.isEnabled || this.listening) return;
    this.listening = true;

    this.swUpdate.versionUpdates.subscribe(event => this.log(event));

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(async () => {
        if (await this.pageRunsLatestBuild()) {
          // Déjà à jour : on aligne le service worker sans rien demander.
          await this.swUpdate.activateUpdate().catch(() => undefined);
          return;
        }
        this.updateAvailable.next(true);
      });
  }

  public updateApplication(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.activateUpdate().then(() => document.location.reload());
    } else {
      document.location.reload();
    }
  }

  /**
   * Le bundle principal chargé par cette page figure-t-il dans le manifeste de
   * la dernière version ? En cas de doute (manifeste illisible, bundle introuvable),
   * on répond non : mieux vaut un bandeau de trop qu'une mise à jour manquée.
   */
  private async pageRunsLatestBuild(): Promise<boolean> {
    try {
      const mainBundle = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
        .map(s => new URL(s.src, document.baseURI).pathname)
        .find(path => /\/main-[A-Za-z0-9]+\.js$/.test(path));
      if (!mainBundle) return false;

      const response = await fetch(`/ngsw.json?ngsw-bypass=true&t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return false;
      const manifest = (await response.json()) as { hashTable?: Record<string, string> };
      return Object.prototype.hasOwnProperty.call(manifest.hashTable ?? {}, mainBundle);
    } catch {
      return false;
    }
  }

  private log(event: VersionEvent): void {
    switch (event.type) {
      case 'VERSION_DETECTED':
        console.log('%cNouvelle version détectée : ' + event.version.hash, 'color: green');
        break;
      case 'VERSION_READY':
        console.log('%cVersion prête : ' + event.latestVersion.hash + ' (actuelle : ' + event.currentVersion.hash + ')', 'color: blue');
        break;
      case 'VERSION_INSTALLATION_FAILED':
        console.log('%cÉchec d\'installation de la version ' + event.version.hash + ' : ' + event.error, 'color: red');
        break;
    }
  }
}
